import React, { useCallback, useEffect, useState, Suspense, lazy, memo, createElement } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2Icon, ArrowDownIcon, XIcon } from 'lucide-react';
import { PurchaseSection } from '../components/PurchaseSection';
import { TestimonialCard } from '../components/TestimonialCard';
import { SocialProofPopup } from '../components/SocialProofPopup';
import { CountdownBanner, CountdownBox } from '../components/CountdownTimer';
// Lazy load below-the-fold components
const KitGallery = lazy(() => import('../components/KitGallery').then(module => ({
  default: module.KitGallery
})));
const MAIN_IMAGE = 'https://cdn.magicpatterns.com/uploads/tiUFwqjSsaFQWpMXkC5s63/ChatGPT_Image_11_mar_2026,_20_14_12_-_Editado.png';
const BONUS_1 = 'https://cdn.magicpatterns.com/uploads/pZpWRmaQYXZYryKHwdxM1y/ChatGPT_Image_12_mar_2026,_15_36_38.png';
const BONUS_2 = 'https://cdn.magicpatterns.com/uploads/pppXs5taX8bVLwNCHYihN6/ChatGPT_Image_12_mar_2026,_15_31_25.png';
const BONUS_3 = 'https://cdn.magicpatterns.com/uploads/7jmkkQBFKpSEt7CPKSA35a/ChatGPT_Image_12_mar_2026,_15_36_58.png';
const BUYER_AVATARS = ['https://randomuser.me/api/portraits/men/32.jpg', 'https://randomuser.me/api/portraits/women/44.jpg', 'https://randomuser.me/api/portraits/men/67.jpg', 'https://randomuser.me/api/portraits/women/17.jpg', 'https://randomuser.me/api/portraits/men/85.jpg'];
const fadeUp = {
  initial: {
    opacity: 0,
    y: 30
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true
  },
  transition: {
    duration: 0.6
  }
};
const staggerItem = {
  initial: {
    opacity: 0,
    y: 20
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true
  }
};
const FAQItem = memo(function FAQItem({
  question,
  answer
}: {
  question: string;
  answer: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const toggleOpen = useCallback(() => setIsOpen(prev => !prev), []);
  return <motion.div {...staggerItem} className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm" data-id="element-143">
      <button onClick={toggleOpen} className="w-full flex items-center justify-between p-5 md:p-6 text-left gap-4" data-id="element-144">
        <span className="text-lg font-semibold text-[#0b1628]" data-id="element-145">{question}</span>
        <motion.span animate={{
        rotate: isOpen ? 45 : 0
      }} transition={{
        duration: 0.2
      }} className="text-teal-500 text-2xl font-bold flex-shrink-0 leading-none" data-id="element-146">
          +
        </motion.span>
      </button>
      <motion.div initial={false} animate={{
      height: isOpen ? 'auto' : 0,
      opacity: isOpen ? 1 : 0
    }} transition={{
      duration: 0.3,
      ease: 'easeInOut'
    }} className="overflow-hidden" data-id="element-147">
        <p className="px-5 md:px-6 pb-5 md:pb-6 text-slate-600 leading-relaxed" data-id="element-148">
          {answer}
        </p>
      </motion.div>
    </motion.div>;
});
export function SalesPage() {
  useEffect(() => {
    document.title = 'Kit de Rendimiento Personal';
    // SEO meta tags
    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"], meta[property="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        if (name.startsWith('og:')) {
          el.setAttribute('property', name);
        } else {
          el.setAttribute('name', name);
        }
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };
    setMeta('description', 'Kit de Rendimiento Personal — Un sistema práctico para organizar tu mente, recuperar claridad y empezar a obtener resultados reales. Método C.S.M. + 3 bonos incluidos.');
    setMeta('og:title', 'Kit de Rendimiento Personal — Método C.S.M.');
    setMeta('og:description', 'Dejá de esforzarte sin dirección. Este sistema te da claridad, orden y resultados desde el primer día.');
    setMeta('og:image', MAIN_IMAGE);
    setMeta('og:type', 'website');
    // Preload critical images
    const preloadImage = (url: string) => {
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'image';
      link.href = url;
      document.head.appendChild(link);
    };
    preloadImage(MAIN_IMAGE);
  }, []);
  const scrollToPurchase = useCallback(() => {
    const element = document.getElementById('purchase-section');
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth'
      });
    }
  }, []);
  return <div className="min-h-screen bg-blue-50/50 text-slate-900 overflow-x-hidden" data-id="element-149">
      <SocialProofPopup data-id="element-150" />

      {/* ============================================ */}
      {/* URGENCY COUNTDOWN BANNER */}
      {/* ============================================ */}
      <CountdownBanner data-id="element-151" />

      {/* ============================================ */}
      {/* 1. HERO — New Structure */}
      {/* ============================================ */}
      <header className="bg-[#0b1628] text-white pt-12 md:pt-20 pb-14 md:pb-24 px-4 relative overflow-hidden" data-id="element-152">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full blur-[120px] -translate-y-1/2 translate-x-1/3 bg-[#142237]" data-id="element-153" />
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-cyan-500/10 rounded-full blur-[80px] translate-y-1/2 -translate-x-1/3" data-id="element-154" />

        <div className="max-w-4xl mx-auto relative z-10 text-center" data-id="element-155">
          {/* [ELEMENTO 2] — TÍTULO PRINCIPAL */}
          <motion.div initial={{
          opacity: 0,
          y: -20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.7
        }} data-id="element-156">
            <h1 className="md:text-6xl lg:text-7xl mb-4 font-[800] text-[28px]" data-id="element-157">
              <span className="text-white" data-id="element-158">
                Dejá de improvisar tu vida. Empezá a{' '}
              </span>
              <span className="text-amber-400" data-id="element-159">construirla.</span>
            </h1>
          </motion.div>

          {/* [ELEMENTO 3] — SUBTÍTULO DE IMPACTO */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.7,
          delay: 0.1
        }} data-id="element-160">
            <p className="md:text-2xl font-semibold mb-8 md:mb-12 max-w-2xl mx-auto text-[16px]" data-id="element-161">
              <span className="text-white" data-id="element-162">
                Si hoy no tenés un sistema claro,{' '}
              </span>
              <span className="text-amber-400" data-id="element-163">en 6 meses</span>
              <span className="text-white" data-id="element-164">
                {' '}
                vas a seguir exactamente donde estás ahora.
              </span>
            </p>
          </motion.div>

          {/* [ELEMENTO 4] — MOCKUP PRINCIPAL */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.92
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.8,
          delay: 0.2
        }} className="relative max-w-xs md:max-w-2xl mx-auto mb-8 md:mb-10 cursor-pointer group" onClick={scrollToPurchase} data-id="element-165">
            <div className="absolute inset-0 bg-teal-500 rounded-2xl blur-3xl opacity-15 group-hover:opacity-25 transition-opacity" data-id="element-166" />
            <img src={MAIN_IMAGE} alt="Kit de Rendimiento Personal" width="800" height="1000" fetchpriority="high" className="relative rounded-2xl shadow-2xl transform group-hover:-translate-y-2 transition-transform duration-300" data-id="element-167" />
          </motion.div>

          {/* [ELEMENTO 5] — DESCRIPCIÓN CON MÉTODO */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.7,
          delay: 0.3
        }} data-id="element-168">
            <p className="text-lg md:text-xl text-slate-300 max-w-2xl mx-auto leading-relaxed mb-8 md:mb-10" data-id="element-169">
              El Método C.S.M. es el sistema que te muestra exactamente qué te
              está frenando, cómo corregirlo y el camino concreto para construir
              la vida que elegiste — sin improvisar más.
            </p>
          </motion.div>

          {/* [ELEMENTO 6] — ÍCONOS DE CONFIANZA */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.7,
          delay: 0.4
        }} className="grid grid-cols-2 md:grid-cols-4 gap-3 max-w-3xl mx-auto mb-8" data-id="element-170">
            <div className="flex items-center justify-center gap-2 bg-[#0f2035] border border-[#1a3050] rounded-lg px-4 py-3" data-id="element-171">
              <span className="text-xl" data-id="element-172">📄</span>
              <span className="text-slate-300 text-sm font-medium" data-id="element-173">
                100% PDF
              </span>
            </div>
            <div className="flex items-center justify-center gap-2 bg-[#0f2035] border border-[#1a3050] rounded-lg px-4 py-3" data-id="element-174">
              <span className="text-xl" data-id="element-175">⚡</span>
              <span className="text-slate-300 text-sm font-medium" data-id="element-176">
                Acceso inmediato
              </span>
            </div>
            <div className="flex items-center justify-center gap-2 bg-[#0f2035] border border-[#1a3050] rounded-lg px-4 py-3" data-id="element-177">
              <span className="text-xl" data-id="element-178">🇦🇷</span>
              <span className="text-slate-300 text-sm font-medium" data-id="element-179">
                Hecho para Argentina
              </span>
            </div>
            <div className="flex items-center justify-center gap-2 bg-[#0f2035] border border-[#1a3050] rounded-lg px-4 py-3" data-id="element-180">
              <span className="text-xl" data-id="element-181">🔒</span>
              <span className="text-slate-300 text-sm font-medium" data-id="element-182">
                Pago 100% seguro
              </span>
            </div>
          </motion.div>

          {/* [ELEMENTO 7] — TEXTO PREVIO AL BOTÓN */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.7,
          delay: 0.5
        }} className="mb-6" data-id="element-183">
            <p className="text-lg md:text-xl font-bold text-white" data-id="element-184">
              Esto tiene solución.
            </p>
            <p className="text-lg md:text-xl font-bold text-teal-400" data-id="element-185">
              Y podés empezar hoy.
            </p>
          </motion.div>

          {/* [ELEMENTO 8] — BOTÓN PRINCIPAL */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.7,
          delay: 0.6
        }} className="mb-8" data-id="element-186">
            <motion.button whileHover={{
            scale: 1.05
          }} whileTap={{
            scale: 0.95
          }} onClick={scrollToPurchase} className="bg-gradient-to-r from-amber-500 to-orange-600 text-white font-bold py-5 px-10 rounded-xl text-lg md:text-xl shadow-lg shadow-amber-500/30 transition-all inline-flex items-center gap-3 hover:brightness-110" data-id="element-187">
              👉 VER PRECIO Y ACCEDER AL KIT
            </motion.button>
          </motion.div>

          {/* [ELEMENTO 9] — GARANTÍA Y SEGURIDAD */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.7,
          delay: 0.7
        }} className="flex flex-wrap items-center justify-center gap-4 md:gap-6 mb-10 text-slate-400 text-xs md:text-sm" data-id="element-188">
            <div className="flex items-center gap-2" data-id="element-189">
              <span data-id="element-190">🛡️</span>
              <span data-id="element-191">Garantía 30 días sin preguntas</span>
            </div>
            <div className="flex items-center gap-2" data-id="element-192">
              <span data-id="element-193">🔒</span>
              <span data-id="element-194">Pago 100% seguro</span>
            </div>
            <div className="flex items-center gap-2" data-id="element-195">
              <span data-id="element-196">💳</span>
              <span data-id="element-197">Mercado Pago | Tarjeta | Transferencia</span>
            </div>
          </motion.div>

          {/* [ELEMENTO 10] — MÉTRICAS DE PRUEBA SOCIAL */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.7,
          delay: 0.8
        }} className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl mx-auto" data-id="element-198">
            <div className="bg-[#0f2035] border border-[#1a3050] rounded-xl p-4 text-center" data-id="element-199">
              <p className="text-2xl md:text-3xl font-extrabold text-teal-400 mb-1" data-id="element-200">
                +100
              </p>
              <p className="text-slate-400 text-xs md:text-sm" data-id="element-201">
                personas ya aplican este sistema
              </p>
            </div>
            <div className="bg-[#0f2035] border border-[#1a3050] rounded-xl p-4 text-center" data-id="element-202">
              <p className="text-2xl md:text-3xl font-extrabold text-teal-400 mb-1" data-id="element-203">
                4
              </p>
              <p className="text-slate-400 text-xs md:text-sm" data-id="element-204">
                materiales completos
              </p>
            </div>
            <div className="bg-[#0f2035] border border-[#1a3050] rounded-xl p-4 text-center" data-id="element-205">
              <p className="text-2xl md:text-3xl font-extrabold text-teal-400 mb-1" data-id="element-206">
                4.9/5
              </p>
              <p className="text-slate-400 text-xs md:text-sm" data-id="element-207">
                calificación ⭐
              </p>
            </div>
            <div className="bg-[#0f2035] border border-[#1a3050] rounded-xl p-4 text-center" data-id="element-208">
              <p className="text-2xl md:text-3xl font-extrabold text-teal-400 mb-1" data-id="element-209">
                🇦🇷
              </p>
              <p className="text-slate-400 text-xs md:text-sm" data-id="element-210">
                Hecho para Argentina
              </p>
            </div>
          </motion.div>
        </div>
      </header>

      {/* ============================================ */}
      {/* [2] PROBLEM LIST SECTION */}
      {/* ============================================ */}
      <section className="bg-white py-20 px-4" data-id="element-211">
        <div className="max-w-3xl mx-auto" data-id="element-212">
          <motion.div {...fadeUp} data-id="element-213">
            <h2 className="text-3xl md:text-4xl font-extrabold text-[#0b1628] mb-12 text-center" data-id="element-214">
              Si querés emprender o crecer profesionalmente, esto te va a sonar
              familiar:
            </h2>
          </motion.div>

          <div className="space-y-4 mb-12" data-id="element-215">
            <motion.div {...staggerItem} className="flex items-start gap-4 bg-red-50/70 p-5 rounded-xl border border-red-200" data-id="element-216">
              <XIcon className="w-6 h-6 text-red-500 flex-shrink-0 mt-0.5" data-id="element-217" />
              <span className="text-lg text-slate-800" data-id="element-218">
                Vas al trabajo todos los días sabiendo que eso no es lo que
                querés para tu vida... pero entre las obligaciones, las facturas
                y el cansancio, nunca encontrás el momento para cambiar algo.
              </span>
            </motion.div>
            <motion.div {...staggerItem} className="flex items-start gap-4 bg-red-50/70 p-5 rounded-xl border border-red-200" data-id="element-219">
              <XIcon className="w-6 h-6 text-red-500 flex-shrink-0 mt-0.5" data-id="element-220" />
              <span className="text-lg text-slate-800" data-id="element-221">
                Tenés ideas, proyectos y ganas... pero cuando llegás a casa
                después del trabajo ya no te queda energía para nada.
              </span>
            </motion.div>
            <motion.div {...staggerItem} className="flex items-start gap-4 bg-red-50/70 p-5 rounded-xl border border-red-200" data-id="element-222">
              <XIcon className="w-6 h-6 text-red-500 flex-shrink-0 mt-0.5" data-id="element-223" />
              <span className="text-lg text-slate-800" data-id="element-224">
                Intentaste organizarte mil veces. Aplicaciones, libretas,
                listas... Empezás fuerte y a las dos semanas estás exactamente
                igual que antes.
              </span>
            </motion.div>

            <motion.div {...staggerItem} className="flex items-start gap-4 bg-red-50/70 p-5 rounded-xl border border-red-200" data-id="element-225">
              <XIcon className="w-6 h-6 text-red-500 flex-shrink-0 mt-0.5" data-id="element-226" />
              <span className="text-lg text-slate-800" data-id="element-227">
                Tus finanzas están descontroladas y eso te genera una presión
                constante que te impide enfocarte en construir lo que realmente
                querés.
              </span>
            </motion.div>
            <motion.div {...staggerItem} className="flex items-start gap-4 bg-red-50/70 p-5 rounded-xl border border-red-200" data-id="element-228">
              <XIcon className="w-6 h-6 text-red-500 flex-shrink-0 mt-0.5" data-id="element-229" />
              <span className="text-lg text-slate-800" data-id="element-230">
                Cada vez que intentás avanzar sin un sistema claro terminás
                frustrado/a. Y esa frustración acumulada te hace dudar si
                realmente tenés lo que hace falta para lograrlo.
              </span>
            </motion.div>
          </div>

          <motion.div {...fadeUp} className="text-center" data-id="element-231">
            <p className="text-2xl md:text-3xl font-extrabold text-[#0b1628]" data-id="element-232">
              ¿Cuánto tiempo más vas a seguir en este ciclo?
            </p>
          </motion.div>
        </div>
      </section>

      {/* ============================================ */}
      {/* 2. OPENING HOOK / AGITATION */}
      {/* ============================================ */}
      <section className="bg-blue-50/50 py-20 px-4" data-id="element-233">
        <div className="max-w-3xl mx-auto" data-id="element-234">
          <motion.div {...fadeUp} className="space-y-6 text-lg md:text-xl text-slate-700 leading-relaxed" data-id="element-235">
            <p className="text-2xl md:text-3xl font-extrabold text-[#0b1628]" data-id="element-236">
              Hay algo que duele más que fracasar.
            </p>

            <p data-id="element-237">
              Es esforzarte todos los días, acostarte cansado/a...y saber que
              mañana va a ser exactamente igual.
            </p>

            <p data-id="element-238">
              Esa sensación de que el tiempo pasa.
              <br data-id="element-239" />
              Que vos seguís en el mismo lugar.
              <br data-id="element-240" />
              Que por más que lo intentás, algo no está funcionando.
            </p>

            <p data-id="element-241">
              Revisás el celular. Abrís listas de tareas.
              <br data-id="element-242" />
              Intentás organizarte. Empezás fuerte.
              <br data-id="element-243" />Y aun así, al final del día...
            </p>

            <p data-id="element-244">
              <span className="font-bold text-[#0b1628]" data-id="element-245">
                algo quedó sin resolver.
              </span>
              <br data-id="element-246" />
              Algo importante que postergaste otra vez.
            </p>

            <p data-id="element-247">
              Y lo peor no es el caos.
              <br data-id="element-248" />
              <span className="font-bold text-[#0b1628]" data-id="element-249">
                Lo peor es que empezás a dudar de vos mismo/a.
              </span>
              <br data-id="element-250" />
              A preguntarte si el problema es tu capacidad.
              <br data-id="element-251" />
              Tu disciplina. Tu fuerza de voluntad.
            </p>

            <p className="font-bold text-[#0b1628]" data-id="element-252">Pero no es nada de eso.</p>

            <div className="bg-[#0b1628] text-white p-8 rounded-2xl my-8 text-center" data-id="element-253">
              <p className="text-xl md:text-2xl font-semibold text-slate-300 mb-3" data-id="element-254">
                El problema es que nadie te dio un sistema real.
              </p>
              <p className="text-2xl md:text-3xl font-extrabold text-teal-400" data-id="element-255">
                Y sin un sistema... este ciclo no para solo.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ============================================ */}
      {/* 3. EL COSTO DE NO HACER NADA */}
      {/* ============================================ */}
      <section className="bg-[#0b1628] text-white py-20 px-4" data-id="element-256">
        <div className="max-w-3xl mx-auto" data-id="element-257">
          <motion.div {...fadeUp} data-id="element-258">
            <h2 className="md:text-4xl font-extrabold mb-12 text-center text-[28px]" data-id="element-259">
              La pregunta que tenés que hacerte hoy
            </h2>
          </motion.div>

          <motion.div {...fadeUp} className="space-y-6 text-lg text-slate-300 leading-relaxed" data-id="element-260">
            <p data-id="element-261">
              No es si querés mejorar tu vida.
              <br data-id="element-262" />
              Eso ya lo sabés.
            </p>

            <p data-id="element-263">
              La pregunta real es:
              <br data-id="element-264" />
              <span className="text-xl font-bold text-white" data-id="element-265">
                ¿Qué pasa si no hacés nada?
              </span>
            </p>

            <p data-id="element-266">Pensalo un segundo.</p>

            <p data-id="element-267">
              Si seguís exactamente igual que ahora...
              <br data-id="element-268" />
              sin un sistema, sin dirección, sin claridad...
            </p>

            <p className="text-xl font-bold text-teal-400" data-id="element-269">
              ¿Cómo va a ser tu vida en 6 meses?
            </p>

            <p data-id="element-270">
              ¿Seguís postergando lo importante?¿Seguís acostándote con esa
              sensación de que el día se fue y no avanzaste?¿Seguís sintiéndote
              saturado/a, corriendo detrás de todo?
            </p>

            <p className="font-bold text-white" data-id="element-271">
              La respuesta incómoda es esta:
            </p>

            <p data-id="element-272">
              Sin un sistema, nada cambia solo.
              <br data-id="element-273" />
              El caos no se ordena con el tiempo.
              <br data-id="element-274" />
              La saturación mental no desaparece con descanso.
              <br data-id="element-275" />
              Los resultados no aparecen con más esfuerzo de lo mismo.
            </p>

            <div className="grid md:grid-cols-2 gap-4 my-10" data-id="element-276">
              <motion.div {...staggerItem} className="bg-red-500/10 border border-red-500/30 rounded-xl p-6" data-id="element-277">
                <div className="flex items-start gap-3" data-id="element-278">
                  <span className="text-red-500 text-2xl font-bold mt-0.5" data-id="element-279">
                    ✕
                  </span>
                  <div data-id="element-280">
                    <p className="font-bold text-white mb-2" data-id="element-281">
                      Exactamente donde estás ahora.
                    </p>
                    <p className="text-slate-300" data-id="element-282">
                      Mismo caos. Misma frustración. Mismas excusas.
                    </p>
                  </div>
                </div>
              </motion.div>

              <motion.div {...staggerItem} className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-6" data-id="element-283">
                <div className="flex items-start gap-3" data-id="element-284">
                  <span className="text-emerald-500 text-2xl font-bold mt-0.5" data-id="element-285">
                    ✅
                  </span>
                  <div data-id="element-286">
                    <p className="font-bold text-white mb-2" data-id="element-287">
                      Con un sistema funcionando.
                    </p>
                    <p className="text-slate-300" data-id="element-288">
                      Con claridad sobre qué hacer cada día. Avanzando hacia lo
                      que realmente querés.
                    </p>
                  </div>
                </div>
              </motion.div>
            </div>

            <div className="text-center mt-12" data-id="element-289">
              <p className="text-xl mb-2" data-id="element-290">
                La diferencia entre esos dos lugares...
              </p>
              <p className="text-2xl font-bold text-teal-400" data-id="element-291">
                empieza con una decisión hoy.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ============================================ */}
      {/* 4. MÉTODO C.S.M. */}
      {/* ============================================ */}
      <section className="bg-blue-50/50 py-20 px-4" data-id="element-292">
        <div className="max-w-3xl mx-auto" data-id="element-293">
          <motion.div {...fadeUp} className="text-center mb-12" data-id="element-294">
            <h2 className="md:text-6xl font-extrabold text-[#0b1628] mb-6 text-[32px]" data-id="element-295">
              Por eso creamos el
              <br data-id="element-296" />
              Método <span className="text-teal-500" data-id="element-297">C.S.M.</span>
            </h2>
            <p className="md:text-2xl font-bold text-slate-700 mb-6 text-[18px]" data-id="element-298">
              C.S.M. significa Claridad → Sistema → Momentum.
            </p>
            <div className="w-24 h-1 bg-teal-500 mx-auto mb-10 rounded-full" data-id="element-299" />
          </motion.div>

          <motion.div {...fadeUp} className="space-y-6 text-lg md:text-xl text-slate-700 leading-relaxed mb-12" data-id="element-300">
            <p data-id="element-301">
              Es una secuencia de 3 fases diseñada específicamente para personas
              que tienen esfuerzo pero no tienen dirección.
            </p>

            <div className="grid gap-4 my-8 max-w-xl mx-auto" data-id="element-302">
              <div className="flex items-start gap-4 bg-white p-5 rounded-xl shadow-sm border border-slate-200" data-id="element-303">
                <span className="text-red-500 text-2xl font-bold mt-0.5" data-id="element-304">
                  ✕
                </span>
                <span data-id="element-305">No es motivación.</span>
              </div>
              <div className="flex items-start gap-4 bg-white p-5 rounded-xl shadow-sm border border-slate-200" data-id="element-306">
                <span className="text-red-500 text-2xl font-bold mt-0.5" data-id="element-307">
                  ✕
                </span>
                <span data-id="element-308">No es productividad genérica.</span>
              </div>
              <div className="flex items-start gap-4 bg-white p-5 rounded-xl shadow-sm border border-slate-200" data-id="element-309">
                <span className="text-red-500 text-2xl font-bold mt-0.5" data-id="element-310">
                  ✕
                </span>
                <span data-id="element-311">No es otra lista de consejos que ya conocés.</span>
              </div>
            </div>

            <p className="font-bold text-[#0b1628] text-center text-xl" data-id="element-312">
              Es un sistema de organización mental que funciona incluso cuando
              estás saturado/a y no sabés por dónde empezar.
            </p>
          </motion.div>

          {/* Phase 1 */}
          <motion.div {...fadeUp} className="mb-10" data-id="element-313">
            <div className="flex items-center gap-4 mb-4" data-id="element-314">
              <div className="w-12 h-12 bg-teal-500 rounded-xl flex items-center justify-center text-white font-extrabold text-xl flex-shrink-0" data-id="element-315">
                1
              </div>
              <h3 className="text-2xl font-bold text-[#0b1628]" data-id="element-316">
                Fase 1 — CLARIDAD
              </h3>
            </div>
            <div className="ml-16 space-y-4 text-lg text-slate-700 leading-relaxed" data-id="element-317">
              <p data-id="element-318">
                Entendés exactamente qué está fallando y por qué.
                <br data-id="element-319" />
                Dejás de adivinar y empezás a ver con precisión.
              </p>
            </div>
          </motion.div>

          {/* Phase 2 */}
          <motion.div {...fadeUp} className="mb-10" data-id="element-320">
            <div className="flex items-center gap-4 mb-4" data-id="element-321">
              <div className="w-12 h-12 bg-teal-500 rounded-xl flex items-center justify-center text-white font-extrabold text-xl flex-shrink-0" data-id="element-322">
                2
              </div>
              <h3 className="text-2xl font-bold text-[#0b1628]" data-id="element-323">
                Fase 2 — SISTEMA
              </h3>
            </div>
            <div className="ml-16 space-y-4 text-lg text-slate-700 leading-relaxed" data-id="element-324">
              <p data-id="element-325">
                Construís una estructura personal que funciona para tu vida
                real. No para la vida ideal de un gurú.
              </p>
            </div>
          </motion.div>

          {/* Phase 3 */}
          <motion.div {...fadeUp} data-id="element-326">
            <div className="flex items-center gap-4 mb-4" data-id="element-327">
              <div className="w-12 h-12 bg-teal-500 rounded-xl flex items-center justify-center text-white font-extrabold text-xl flex-shrink-0" data-id="element-328">
                3
              </div>
              <h3 className="text-2xl font-bold text-[#0b1628]" data-id="element-329">
                Fase 3 — MOMENTUM
              </h3>
            </div>
            <div className="ml-16 space-y-4 text-lg text-slate-700 leading-relaxed" data-id="element-330">
              <p data-id="element-331">
                Aprendés a mantenerlo para no volver al caos.
                <br data-id="element-332" />
                Porque el sistema que no se sostiene, no sirve.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ============================================ */}
      {/* PÁGINAS INTERNAS — Preview del contenido */}
      {/* ============================================ */}
      <section className="bg-white py-12 px-4" data-id="element-333">
        <div className="max-w-5xl mx-auto" data-id="element-334">
          <motion.div {...fadeUp} className="text-center mb-8" data-id="element-335">
            <h2 className="text-3xl md:text-4xl font-extrabold text-[#0b1628] mb-3" data-id="element-336">
              Una mirada adentro del kit y los bonos
            </h2>
            <p className="text-base text-slate-600 max-w-2xl mx-auto" data-id="element-337">
              Así se ve el contenido que vas a recibir. Hacé click en cualquier
              imagen para verla en detalle.
            </p>
          </motion.div>

          <Suspense fallback={<div className="flex items-center justify-center py-12" data-id="element-339">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500" data-id="element-340"></div>
              </div>} data-id="element-338">
            <KitGallery data-id="element-341" />
          </Suspense>

          <motion.div {...fadeUp} className="text-center mt-8" data-id="element-342">
            <p className="text-base md:text-lg text-slate-700 leading-relaxed max-w-2xl mx-auto" data-id="element-343">
              Todo explicado de forma simple y directa. Sin relleno. Sin teoría
              innecesaria. Solo lo que necesitás para empezar a aplicarlo.
              Además, es{' '}
              <span className="font-bold text-teal-600" data-id="element-344">
                100% digital, fácil de descargar y podés acceder inmediatamente
              </span>{' '}
              desde tu celular, tablet o computadora.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ============================================ */}
      {/* 8. WHAT'S INCLUDED (Kept as is) */}
      {/* ============================================ */}
      <section className="bg-[#0b1628] text-white py-24 px-4" data-id="element-345">
        <div className="max-w-4xl mx-auto" data-id="element-346">
          <motion.div {...fadeUp} className="text-center mb-16" data-id="element-347">
            <h2 className="text-3xl md:text-4xl font-extrabold mb-4" data-id="element-348">
              Qué incluye el KIT DE RENDIMIENTO PERSONAL
            </h2>
            <p className="text-slate-400 text-lg" data-id="element-349">
              Al acceder recibirás una guía completa con +300 páginas que
              incluye:
            </p>
          </motion.div>

          {/* Main Ebook */}
          <motion.div {...fadeUp} className="bg-[#0f2035] border border-[#1a3050] rounded-2xl overflow-hidden mb-8" data-id="element-350">
            <div className="grid md:grid-cols-2 gap-0" data-id="element-351">
              <div className="p-8 md:p-10 flex flex-col justify-center" data-id="element-352">
                <span className="text-teal-400 text-sm font-bold uppercase tracking-wider mb-3" data-id="element-353">
                  📘 Guía principal
                </span>
                <h3 className="text-2xl md:text-3xl font-extrabold text-white mb-6" data-id="element-354">
                  KIT DE RENDIMIENTO PERSONAL
                </h3>
                <ul className="space-y-3" data-id="element-355">
                  <li className="flex items-center gap-3" data-id="element-356">
                    <CheckCircle2Icon className="w-5 h-5 text-teal-400 flex-shrink-0" data-id="element-357" />
                    <span className="text-slate-200" data-id="element-358">
                      Explicación clara del problema
                    </span>
                  </li>
                  <li className="flex items-center gap-3" data-id="element-359">
                    <CheckCircle2Icon className="w-5 h-5 text-teal-400 flex-shrink-0" data-id="element-360" />
                    <span className="text-slate-200" data-id="element-361">Sistema paso a paso</span>
                  </li>
                  <li className="flex items-center gap-3" data-id="element-362">
                    <CheckCircle2Icon className="w-5 h-5 text-teal-400 flex-shrink-0" data-id="element-363" />
                    <span className="text-slate-200" data-id="element-364">Ejercicios prácticos</span>
                  </li>
                  <li className="flex items-center gap-3" data-id="element-365">
                    <CheckCircle2Icon className="w-5 h-5 text-teal-400 flex-shrink-0" data-id="element-366" />
                    <span className="text-slate-200" data-id="element-367">
                      Herramientas para organizar tu vida
                    </span>
                  </li>
                  <li className="flex items-center gap-3" data-id="element-368">
                    <CheckCircle2Icon className="w-5 h-5 text-teal-400 flex-shrink-0" data-id="element-369" />
                    <span className="text-slate-200" data-id="element-370">
                      Estrategias para mejorar tus resultados
                    </span>
                  </li>
                </ul>
                <p className="text-slate-400 text-sm mt-6 italic" data-id="element-371">
                  Todo explicado de forma simple para que puedas aplicarlo en tu
                  vida diaria.
                </p>
              </div>
              <div className="p-6 flex items-center justify-center bg-[#0f2035]/50" data-id="element-372">
                <img src={MAIN_IMAGE} alt="Ebook Kit de Rendimiento Personal" loading="lazy" className="rounded-xl shadow-lg max-h-80 object-contain" data-id="element-373" />
              </div>
            </div>
          </motion.div>

          {/* Bonuses Header */}
          <motion.div {...fadeUp} className="text-center my-12" data-id="element-374">
            <span className="bg-red-500 text-white px-5 py-1.5 rounded-full font-bold uppercase tracking-wide text-[12px]" data-id="element-375">
              Además recibirás 3 bonos especiales
            </span>
          </motion.div>

          {/* Bonuses Grid — with values */}
          <div className="grid md:grid-cols-3 gap-6" data-id="element-376">
            {/* Bono 1 */}
            <motion.div {...fadeUp} className="bg-[#0f2035] border border-[#1a3050] rounded-2xl overflow-hidden flex flex-col" data-id="element-377">
              <div className="aspect-[3/4] overflow-hidden" data-id="element-378">
                <img src={BONUS_1} alt="Bono 1 - Reset Personal" loading="lazy" className="w-full h-full object-cover" data-id="element-379" />
              </div>
              <div className="p-6 flex-1 flex flex-col" data-id="element-380">
                <div className="flex items-center justify-between mb-3" data-id="element-381">
                  <span className="inline-block bg-red-500/20 text-red-400 px-3 py-1 rounded-full text-xs font-bold uppercase" data-id="element-382">
                    🎁 Bono 1
                  </span>
                  <div className="text-right" data-id="element-383">
                    <p className="text-slate-500 text-sm line-through" data-id="element-384">
                      $8.990
                    </p>
                    <p className="text-emerald-400 text-sm font-bold" data-id="element-385">GRATIS</p>
                  </div>
                </div>
                <h4 className="text-xl font-bold text-white mb-2" data-id="element-386">
                  RESET PERSONAL
                </h4>
                <p className="text-slate-400 text-sm mb-4" data-id="element-387">
                  Una guía para analizar tu situación actual y reorganizar tu
                  vida cuando todo parece caótico.
                </p>
                <p className="text-slate-500 text-xs mb-3" data-id="element-388">
                  Incluye ejercicios para:
                </p>
                <ul className="space-y-2 text-sm" data-id="element-389">
                  <li className="flex items-start gap-2" data-id="element-390">
                    <CheckCircle2Icon className="w-4 h-4 text-teal-400 flex-shrink-0 mt-0.5" data-id="element-391" />
                    <span className="text-slate-300" data-id="element-392">
                      Identificar lo que no está funcionando
                    </span>
                  </li>
                  <li className="flex items-start gap-2" data-id="element-393">
                    <CheckCircle2Icon className="w-4 h-4 text-teal-400 flex-shrink-0 mt-0.5" data-id="element-394" />
                    <span className="text-slate-300" data-id="element-395">
                      Redefinir prioridades
                    </span>
                  </li>
                  <li className="flex items-start gap-2" data-id="element-396">
                    <CheckCircle2Icon className="w-4 h-4 text-teal-400 flex-shrink-0 mt-0.5" data-id="element-397" />
                    <span className="text-slate-300" data-id="element-398">
                      Construir un nuevo punto de partida
                    </span>
                  </li>
                </ul>
              </div>
            </motion.div>

            {/* Bono 2 */}
            <motion.div {...fadeUp} className="bg-[#0f2035] border border-[#1a3050] rounded-2xl overflow-hidden flex flex-col" data-id="element-399">
              <div className="aspect-[3/4] overflow-hidden" data-id="element-400">
                <img src={BONUS_2} alt="Bono 2 - Mente en Calma" loading="lazy" className="w-full h-full object-cover" data-id="element-401" />
              </div>
              <div className="p-6 flex-1 flex flex-col" data-id="element-402">
                <div className="flex items-center justify-between mb-3" data-id="element-403">
                  <span className="inline-block bg-red-500/20 text-red-400 px-3 py-1 rounded-full text-xs font-bold uppercase" data-id="element-404">
                    🎁 Bono 2
                  </span>
                  <div className="text-right" data-id="element-405">
                    <p className="text-slate-500 text-sm line-through" data-id="element-406">
                      $7.990
                    </p>
                    <p className="text-emerald-400 text-sm font-bold" data-id="element-407">GRATIS</p>
                  </div>
                </div>
                <h4 className="text-xl font-bold text-white mb-2" data-id="element-408">
                  MENTE EN CALMA
                </h4>
                <p className="text-slate-400 text-sm mb-4" data-id="element-409">
                  Un material diseñado para ayudarte a reducir el ruido mental y
                  recuperar tranquilidad en tu día a día.
                </p>
                <p className="text-slate-500 text-xs mb-3" data-id="element-410">
                  Aprenderás técnicas simples para:
                </p>
                <ul className="space-y-2 text-sm" data-id="element-411">
                  <li className="flex items-start gap-2" data-id="element-412">
                    <CheckCircle2Icon className="w-4 h-4 text-teal-400 flex-shrink-0 mt-0.5" data-id="element-413" />
                    <span className="text-slate-300" data-id="element-414">
                      Reducir sobrepensamiento
                    </span>
                  </li>
                  <li className="flex items-start gap-2" data-id="element-415">
                    <CheckCircle2Icon className="w-4 h-4 text-teal-400 flex-shrink-0 mt-0.5" data-id="element-416" />
                    <span className="text-slate-300" data-id="element-417">Ejercicios mentales</span>
                  </li>
                  <li className="flex items-start gap-2" data-id="element-418">
                    <CheckCircle2Icon className="w-4 h-4 text-teal-400 flex-shrink-0 mt-0.5" data-id="element-419" />
                    <span className="text-slate-300" data-id="element-420">Recuperar claridad</span>
                  </li>
                </ul>
              </div>
            </motion.div>

            {/* Bono 3 */}
            <motion.div {...fadeUp} className="bg-[#0f2035] border border-[#1a3050] rounded-2xl overflow-hidden flex flex-col" data-id="element-421">
              <div className="aspect-[3/4] overflow-hidden" data-id="element-422">
                <img src={BONUS_3} alt="Bono 3 - Control Total" loading="lazy" className="w-full h-full object-cover" data-id="element-423" />
              </div>
              <div className="p-6 flex-1 flex flex-col" data-id="element-424">
                <div className="flex items-center justify-between mb-3" data-id="element-425">
                  <span className="inline-block bg-red-500/20 text-red-400 px-3 py-1 rounded-full text-xs font-bold uppercase" data-id="element-426">
                    🎁 Bono 3
                  </span>
                  <div className="text-right" data-id="element-427">
                    <p className="text-slate-500 text-sm line-through" data-id="element-428">
                      $8.990
                    </p>
                    <p className="text-emerald-400 text-sm font-bold" data-id="element-429">GRATIS</p>
                  </div>
                </div>
                <h4 className="text-xl font-bold text-white mb-2" data-id="element-430">
                  CONTROL TOTAL
                </h4>
                <p className="text-slate-400 text-sm mb-4" data-id="element-431">
                  Un sistema práctico para organizar tu trabajo y tus finanzas y
                  recuperar el control de tu vida.
                </p>
                <p className="text-slate-500 text-xs mb-3" data-id="element-432">
                  Aprenderás técnicas simples para:
                </p>
                <ul className="space-y-2 text-sm" data-id="element-433">
                  <li className="flex items-start gap-2" data-id="element-434">
                    <CheckCircle2Icon className="w-4 h-4 text-teal-400 flex-shrink-0 mt-0.5" data-id="element-435" />
                    <span className="text-slate-300" data-id="element-436">
                      Organizar tu trabajo y responsabilidades
                    </span>
                  </li>
                  <li className="flex items-start gap-2" data-id="element-437">
                    <CheckCircle2Icon className="w-4 h-4 text-teal-400 flex-shrink-0 mt-0.5" data-id="element-438" />
                    <span className="text-slate-300" data-id="element-439">
                      Ordenar tus finanzas personales
                    </span>
                  </li>
                  <li className="flex items-start gap-2" data-id="element-440">
                    <CheckCircle2Icon className="w-4 h-4 text-teal-400 flex-shrink-0 mt-0.5" data-id="element-441" />
                    <span className="text-slate-300" data-id="element-442">
                      Recuperar una sensación real de control
                    </span>
                  </li>
                </ul>
              </div>
            </motion.div>
          </div>

          {/* [3] BONUS TOTALS BLOCK */}
          <motion.div {...fadeUp} className="mt-10 bg-[#0f2035] border-2 border-emerald-500/30 rounded-2xl p-8 text-center" data-id="element-443">
            <p className="text-slate-400 text-lg mb-2" data-id="element-444">
              Valor total de los 3 bonos:
            </p>
            <p className="text-3xl md:text-4xl font-extrabold text-white mb-3" data-id="element-445">
              $25.970
            </p>
            <p className="text-2xl font-bold text-emerald-400 mb-2" data-id="element-446">
              🎁 GRATIS — Solo con tu Kit
            </p>
            <p className="text-slate-400 text-sm" data-id="element-447">
              Incluidos en todas las ofertas.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ============================================ */}
      {/* [4] SIN/CON COMPARISON TABLE */}
      {/* ============================================ */}
      <section className="bg-blue-50/50 py-20 px-4" data-id="element-448">
        <div className="max-w-4xl mx-auto" data-id="element-449">
          <motion.div {...fadeUp} data-id="element-450">
            <h2 className="text-3xl md:text-4xl font-extrabold text-[#0b1628] mb-12 text-center" data-id="element-451">
              ¿Cuánto te cuesta realmente NO tenerlo?
            </h2>
          </motion.div>

          <motion.div {...fadeUp} className="overflow-hidden rounded-2xl border-2 border-slate-300 shadow-xl" data-id="element-452">
            {/* Table Header */}
            <div className="grid md:grid-cols-2 bg-[#0b1628]" data-id="element-453">
              <div className="bg-red-900/40 border-r border-slate-700 p-4 md:p-6 text-center" data-id="element-454">
                <h3 className="text-xl md:text-2xl font-extrabold text-red-300" data-id="element-455">
                  SIN EL KIT
                </h3>
              </div>
              <div className="bg-emerald-900/40 p-4 md:p-6 text-center" data-id="element-456">
                <h3 className="text-xl md:text-2xl font-extrabold text-emerald-300" data-id="element-457">
                  CON EL KIT
                </h3>
              </div>
            </div>

            {/* Table Rows */}
            <div className="grid md:grid-cols-2 border-b border-slate-300" data-id="element-458">
              <div className="bg-red-50/50 border-r border-slate-300 p-5 md:p-6 flex items-center gap-3" data-id="element-459">
                <XIcon className="w-5 h-5 text-red-500 flex-shrink-0" data-id="element-460" />
                <p className="text-slate-800" data-id="element-461">
                  No sé por dónde empezar a cambiar mi vida
                </p>
              </div>
              <div className="bg-emerald-50/50 p-5 md:p-6 flex items-center gap-3" data-id="element-462">
                <CheckCircle2Icon className="w-5 h-5 text-emerald-600 flex-shrink-0" data-id="element-463" />
                <p className="text-slate-800" data-id="element-464">
                  Sabés exactamente cuál es el primer paso desde el día 1
                </p>
              </div>
            </div>

            <div className="grid md:grid-cols-2 border-b border-slate-300" data-id="element-465">
              <div className="bg-red-50/50 border-r border-slate-300 p-5 md:p-6 flex items-center gap-3" data-id="element-466">
                <XIcon className="w-5 h-5 text-red-500 flex-shrink-0" data-id="element-467" />
                <p className="text-slate-800" data-id="element-468">
                  No entiendo por qué me salen mal las cosas
                </p>
              </div>
              <div className="bg-emerald-50/50 p-5 md:p-6 flex items-center gap-3" data-id="element-469">
                <CheckCircle2Icon className="w-5 h-5 text-emerald-600 flex-shrink-0" data-id="element-470" />
                <p className="text-slate-800" data-id="element-471">
                  Identificás con precisión dónde está la falla y cómo
                  corregirla
                </p>
              </div>
            </div>

            <div className="grid md:grid-cols-2 border-b border-slate-300" data-id="element-472">
              <div className="bg-red-50/50 border-r border-slate-300 p-5 md:p-6 flex items-center gap-3" data-id="element-473">
                <XIcon className="w-5 h-5 text-red-500 flex-shrink-0" data-id="element-474" />
                <p className="text-slate-800" data-id="element-475">
                  Mi vida es un desastre, el estrés me paraliza y eso me frena
                  el cambio
                </p>
              </div>
              <div className="bg-emerald-50/50 p-5 md:p-6 flex items-center gap-3" data-id="element-476">
                <CheckCircle2Icon className="w-5 h-5 text-emerald-600 flex-shrink-0" data-id="element-477" />
                <p className="text-slate-800" data-id="element-478">
                  El cambio es claro, estructurado y genera resultados reales
                </p>
              </div>
            </div>

            <div className="grid md:grid-cols-2 border-b border-slate-300" data-id="element-479">
              <div className="bg-red-50/50 border-r border-slate-300 p-5 md:p-6 flex items-center gap-3" data-id="element-480">
                <XIcon className="w-5 h-5 text-red-500 flex-shrink-0" data-id="element-481" />
                <p className="text-slate-800" data-id="element-482">
                  Mis finanzas son un caos, tengo deudas y no sé adónde va mi
                  dinero
                </p>
              </div>
              <div className="bg-emerald-50/50 p-5 md:p-6 flex items-center gap-3" data-id="element-483">
                <CheckCircle2Icon className="w-5 h-5 text-emerald-600 flex-shrink-0" data-id="element-484" />
                <p className="text-slate-800" data-id="element-485">
                  Proyectás, ordenás y sabés exactamente adónde va tu plata y
                  adónde va tu vida
                </p>
              </div>
            </div>

            <div className="grid md:grid-cols-2" data-id="element-486">
              <div className="bg-red-50/50 border-r border-slate-300 p-5 md:p-6 flex items-center gap-3" data-id="element-487">
                <XIcon className="w-5 h-5 text-red-500 flex-shrink-0" data-id="element-488" />
                <p className="text-slate-800" data-id="element-489">
                  Cada intento sin sistema termina en frustración y más dudas
                  sobre vos mismo
                </p>
              </div>
              <div className="bg-emerald-50/50 p-5 md:p-6 flex items-center gap-3" data-id="element-490">
                <CheckCircle2Icon className="w-5 h-5 text-emerald-600 flex-shrink-0" data-id="element-491" />
                <p className="text-slate-800" data-id="element-492">
                  Tenés un sistema que funciona incluso cuando estás saturado/a
                  y sin motivación
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ============================================ */}
      {/* [5] RESULTS SECTION — Updated benefits */}
      {/* ============================================ */}
      <section className="bg-white py-20 px-4" data-id="element-493">
        <div className="max-w-3xl mx-auto" data-id="element-494">
          <motion.div {...fadeUp} data-id="element-495">
            <h2 className="text-3xl md:text-4xl font-extrabold text-[#0b1628] mb-4 text-center" data-id="element-496">
              Qué puede cambiar cuando aplicás este sistema
            </h2>
            <p className="text-center text-slate-600 text-lg mb-12" data-id="element-497">
              Cuando empieces a aplicar estas herramientas vas a notar cambios
              importantes.
            </p>
          </motion.div>

          <div className="space-y-4 mb-12" data-id="element-498">
            <motion.div {...staggerItem} className="flex items-start gap-4 bg-blue-50/70 p-5 rounded-xl border border-slate-200" data-id="element-499">
              <CheckCircle2Icon className="w-6 h-6 text-teal-500 flex-shrink-0 mt-0.5" data-id="element-500" />
              <span className="text-lg text-slate-800" data-id="element-501">
                Vas a arrancar cada mañana sabiendo exactamente qué hacer
                primero. Sin parálisis. Sin dudas. Sin perder la primera hora
                del día en decidir por dónde empezar.
              </span>
            </motion.div>
            <motion.div {...staggerItem} className="flex items-start gap-4 bg-blue-50/70 p-5 rounded-xl border border-slate-200" data-id="element-502">
              <CheckCircle2Icon className="w-6 h-6 text-teal-500 flex-shrink-0 mt-0.5" data-id="element-503" />
              <span className="text-lg text-slate-800" data-id="element-504">
                Vas a dejar de acostarte con esa sensación de "hice mucho pero
                no avancé nada que realmente importa."
              </span>
            </motion.div>
            <motion.div {...staggerItem} className="flex items-start gap-4 bg-blue-50/70 p-5 rounded-xl border border-slate-200" data-id="element-505">
              <CheckCircle2Icon className="w-6 h-6 text-teal-500 flex-shrink-0 mt-0.5" data-id="element-506" />
              <span className="text-lg text-slate-800" data-id="element-507">
                Vas a tener una dirección clara hacia tu crecimiento profesional
                en lugar de energía dispersa en mil cosas que no te acercan a
                donde querés llegar.
              </span>
            </motion.div>
            <motion.div {...staggerItem} className="flex items-start gap-4 bg-blue-50/70 p-5 rounded-xl border border-slate-200" data-id="element-508">
              <CheckCircle2Icon className="w-6 h-6 text-teal-500 flex-shrink-0 mt-0.5" data-id="element-509" />
              <span className="text-lg text-slate-800" data-id="element-510">
                En 30 días vas a mirar para atrás y ver más progreso real hacia
                lo que querés construir que en los últimos 6 meses.
              </span>
            </motion.div>
          </div>

          <motion.div {...fadeUp} className="text-center space-y-3" data-id="element-511">
            <p className="text-lg text-slate-600" data-id="element-512">No porque trabajes más…</p>
            <p className="text-2xl font-extrabold text-[#0b1628]" data-id="element-513">
              sino porque tendrás dirección.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ============================================ */}
      {/* [6] IMAGINE SECTION — Updated text */}
      {/* ============================================ */}
      <section className="bg-[#0b1628] text-white py-20 px-4" data-id="element-514">
        <div className="max-w-3xl mx-auto" data-id="element-515">
          <motion.div {...fadeUp} data-id="element-516">
            <h2 className="text-3xl md:text-4xl font-extrabold mb-12 text-center" data-id="element-517">
              Imaginá esto...
            </h2>
          </motion.div>

          <motion.div {...fadeUp} className="space-y-6 text-lg text-slate-300 leading-relaxed mb-12" data-id="element-518">
            <p data-id="element-519">
              Son las 8 de la mañana.
              <br data-id="element-520" />
              Abrís el día y sabés exactamente qué tenés que hacer para
              acercarte a lo que querés construir.
            </p>

            <p data-id="element-521">No tenés esa sensación de caos antes de empezar.</p>

            <p className="text-white font-semibold text-xl" data-id="element-522">
              Tenés un sistema.
              <br data-id="element-523" />
              Tenés claridad.
              <br data-id="element-524" />
              Tenés dirección.
            </p>

            <p data-id="element-525">
              A la noche, cuando te acostás, no sentís culpa ni frustración.
              <br data-id="element-526" />
              Sentís que avanzaste un paso más hacia la vida que elegiste.
            </p>
          </motion.div>

          <motion.div {...fadeUp} className="text-center space-y-4 text-lg text-slate-300" data-id="element-527">
            <p data-id="element-528">Eso no es un sueño lejano.</p>
            <p className="text-2xl font-bold text-teal-400" data-id="element-529">
              Es lo que pasa cuando dejás de improvisar y empezás a tener un
              sistema real.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ============================================ */}
      {/* FINAL CTA WITH OFFER */}
      {/* ============================================ */}
      <section className="bg-white py-20 px-4" data-id="element-530">
        <div className="max-w-3xl mx-auto text-center" data-id="element-531">
          <motion.div {...fadeUp} data-id="element-532">
            <p className="text-4xl mb-6" data-id="element-533">🚀</p>
            <h2 className="text-3xl md:text-4xl font-extrabold text-[#0b1628] mb-6" data-id="element-534">
              Accedé ahora al KIT DE RENDIMIENTO PERSONAL
            </h2>

            <p className="text-lg text-slate-600 mb-4 max-w-xl mx-auto" data-id="element-535">
              Si sentís que estás trabajando duro pero no estás logrando los
              resultados que querés…
            </p>
            <p className="text-xl font-bold text-[#0b1628] mb-10" data-id="element-536">
              este sistema puede ayudarte.
            </p>
          </motion.div>

          {/* Offer breakdown */}
          <motion.div {...fadeUp} className="bg-[#0b1628] rounded-2xl p-8 md:p-10 mb-12 max-w-xl mx-auto" data-id="element-537">
            <p className="text-teal-400 uppercase tracking-widest text-sm font-bold mb-6" data-id="element-538">
              Esto es lo que recibís hoy
            </p>
            <div className="space-y-4 text-left mb-8" data-id="element-539">
              <div className="flex items-center gap-3" data-id="element-540">
                <CheckCircle2Icon className="w-5 h-5 text-teal-400 flex-shrink-0" data-id="element-541" />
                <span className="text-slate-200 text-lg" data-id="element-542">
                  📘 Kit de Rendimiento Personal — Guía principal
                </span>
              </div>
              <div className="flex items-center gap-3" data-id="element-543">
                <CheckCircle2Icon className="w-5 h-5 text-teal-400 flex-shrink-0" data-id="element-544" />
                <span className="text-slate-200 text-lg" data-id="element-545">
                  🎁 Bono 1 — Reset Personal
                </span>
              </div>
              <div className="flex items-center gap-3" data-id="element-546">
                <CheckCircle2Icon className="w-5 h-5 text-teal-400 flex-shrink-0" data-id="element-547" />
                <span className="text-slate-200 text-lg" data-id="element-548">
                  🎁 Bono 2 — Mente en Calma
                </span>
              </div>
              <div className="flex items-center gap-3" data-id="element-549">
                <CheckCircle2Icon className="w-5 h-5 text-teal-400 flex-shrink-0" data-id="element-550" />
                <span className="text-slate-200 text-lg" data-id="element-551">
                  🎁 Bono 3 — Control Total
                </span>
              </div>
            </div>

            <div className="border-t border-slate-700 pt-6" data-id="element-552">
              <p className="text-slate-400 text-lg line-through decoration-red-500 decoration-2 mb-1" data-id="element-553">
                Antes $39.990 ARS
              </p>
              <p className="text-amber-400 text-4xl md:text-5xl font-extrabold mb-2" data-id="element-554">
                $14.990 ARS
              </p>
              <span className="inline-block bg-red-500/20 text-red-400 px-4 py-1 rounded-full text-sm font-bold" data-id="element-555">
                62% OFF — Oferta limitada
              </span>
            </div>
          </motion.div>

          <motion.div {...fadeUp} className="space-y-6" data-id="element-556">
            <div className="text-center" data-id="element-557">
              <p className="text-lg font-bold text-[#0b1628]" data-id="element-558">
                Esto tiene solución.
              </p>
              <p className="text-xl font-bold text-teal-500" data-id="element-559">
                Y podés empezar hoy.
              </p>
            </div>
            <motion.button whileHover={{
            scale: 1.05
          }} whileTap={{
            scale: 0.95
          }} onClick={scrollToPurchase} className="bg-amber-500 hover:bg-amber-600 text-white font-bold py-5 px-10 rounded-xl text-xl shadow-lg shadow-amber-500/30 transition-all inline-flex items-center gap-3" data-id="element-560">
              👉 ACCEDER AL KIT
            </motion.button>
          </motion.div>
        </div>
      </section>

      {/* ============================================ */}
      {/* [8] TESTIMONIALS — Updated */}
      {/* ============================================ */}
      <section className="bg-blue-50/50 py-20 px-4" data-id="element-561">
        <div className="max-w-3xl mx-auto" data-id="element-562">
          <motion.div {...fadeUp} data-id="element-563">
            <h3 className="text-3xl font-bold text-center mb-12 text-[#0b1628]" data-id="element-564">
              Lo que dicen quienes ya lo aplicaron
            </h3>
          </motion.div>
          <div className="grid gap-6" data-id="element-565">
            <TestimonialCard name="Gastón R." role="Empleado" city="Rosario" quote="Llevaba años queriendo arrancar algo propio pero entre el trabajo y el cansancio nunca llegaba. Lo que más me sirvió fue la parte de identificar la falla. Entendí exactamente qué me estaba frenando. Eso solo cambió todo." delay={0.1} data-id="element-566" />
            <TestimonialCard name="Florencia M." role="Diseñadora freelance" city="Buenos Aires" quote="Compré medio desconfiada, ya había probado mil métodos. Pero el Reset Personal me hizo ver que estaba poniendo energía en todo menos en lo que me iba a acercar a crecer. Ahora tengo foco real." delay={0.2} data-id="element-567" />
            <TestimonialCard name="Nicolás P." role="Emprendedor" city="Córdoba" quote="Tenía proyectos parados hace meses. El problema no era motivación, era que no tenía un sistema claro. El Método C.S.M. me ordenó la cabeza en una semana. En 6 meses sin esto hubiera seguido igual." delay={0.3} data-id="element-568" />
            <TestimonialCard name="Valentina S." role="Community Manager" city="Mendoza" quote="Lo del Control Total con las finanzas me voló la cabeza. No esperaba que un kit de rendimiento personal tocara ese tema tan bien. Ahora sé exactamente adónde va mi plata y eso me quitó una presión enorme." delay={0.4} data-id="element-569" />
            <TestimonialCard name="Diego L." role="Vendedor" city="La Plata" quote="Nunca escribo reseñas pero esta vez sí. Hace meses que quería emprender algo y siempre postergaba. El kit me dio el sistema que necesitaba para dejar de postergar y empezar a moverme de verdad." delay={0.5} data-id="element-570" />
          </div>
        </div>
      </section>

      {/* ============================================ */}
      {/* URGENCY BLOCK */}
      {/* ============================================ */}
      <section className="bg-blue-50/50 py-16 px-4" data-id="element-571">
        <div className="max-w-2xl mx-auto text-center" data-id="element-572">
          <motion.div {...fadeUp} className="text-xl md:text-2xl text-slate-700 leading-relaxed" data-id="element-573">
            <p data-id="element-574">
              Cada semana que pasa sin un sistema es una semana más de caos,
              postergación y resultados que no llegan.
            </p>
            <br data-id="element-575" />
            <p data-id="element-576">
              No porque no tengas capacidad.
              <br data-id="element-577" />
              Sino porque estás corriendo sin dirección.
            </p>
            <br data-id="element-578" />
            <p className="font-bold text-[#0b1628]" data-id="element-579">
              El cambio está a tu alcance.
            </p>
            <p className="font-bold text-teal-500 text-2xl mt-2" data-id="element-580">
              Y podés empezar hoy.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ============================================ */}
      {/* [7] SPECIAL OFFER URGENCY — Updated text */}
      {/* ============================================ */}
      <section className="bg-gradient-to-b from-[#0b1628] to-[#0f2035] text-white py-20 px-4 relative overflow-hidden" data-id="element-581">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-amber-500/8 rounded-full blur-[150px]" data-id="element-582" />

        <div className="max-w-2xl mx-auto relative z-10" data-id="element-583">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.7
        }} className="text-center" data-id="element-584">
            <div className="flex items-center justify-center gap-4 mb-10" data-id="element-585">
              <div className="h-px w-16 bg-gradient-to-r from-transparent to-amber-500/50" data-id="element-586" />
              <span className="text-amber-400 text-sm font-bold uppercase tracking-[0.2em]" data-id="element-587">
                ⏰ Precio de lanzamiento — Solo esta semana
              </span>
              <div className="h-px w-16 bg-gradient-to-l from-transparent to-amber-500/50" data-id="element-588" />
            </div>

            <h2 className="text-3xl md:text-5xl font-extrabold leading-tight mb-6" data-id="element-589">
              Podés seguir como estás...
              <br data-id="element-590" />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-orange-400" data-id="element-591">
                o aprovechar este momento
              </span>
              <br data-id="element-592" />
              para empezar a cambiarlo.
            </h2>

            <div className="w-20 h-1 bg-gradient-to-r from-amber-500 to-orange-500 mx-auto rounded-full mb-8" data-id="element-593" />

            <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.3
          }} className="bg-[#0f2035] border border-amber-500/30 rounded-2xl p-8 md:p-10 mb-8 shadow-lg shadow-amber-500/10" data-id="element-594">
              <p className="text-xl md:text-2xl text-slate-200 leading-relaxed mb-6" data-id="element-595">
                El Kit de Rendimiento Personal está en su primera versión.
              </p>
              <p className="text-lg text-slate-300 mb-6" data-id="element-596">
                Los que compren ahora tienen acceso garantizado a todas las
                actualizaciones y mejoras futuras sin pagar nada extra.
              </p>
              <p className="text-lg text-slate-300 mb-6" data-id="element-597">
                Cuando lancemos la versión mejorada, el precio sube y no vuelve
                a bajar.
              </p>
              <p className="text-xl font-bold text-amber-400 mb-6" data-id="element-598">
                Esta es la única oportunidad de entrar al precio más bajo con
                acceso de por vida a todo lo que viene.
              </p>

              <CountdownBox data-id="element-599" />

              <p className="text-red-400 text-sm font-semibold animate-pulse" data-id="element-600">
                🔥 Esta oferta puede desaparecer en cualquier momento
              </p>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: -10
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.5
          }} className="flex justify-center" data-id="element-601">
              <motion.div animate={{
              y: [0, 8, 0]
            }} transition={{
              duration: 1.5,
              repeat: Infinity,
              ease: 'easeInOut'
            }} data-id="element-602">
                <ArrowDownIcon className="w-8 h-8 text-amber-400" data-id="element-603" />
              </motion.div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ============================================ */}
      {/* [13] FINAL CLOSING BEFORE PRICES */}
      {/* ============================================ */}
      <section className="bg-blue-50/50 py-16 px-4" data-id="element-604">
        <div className="max-w-2xl mx-auto text-center" data-id="element-605">
          <motion.div {...fadeUp} className="text-xl md:text-2xl text-slate-700 leading-relaxed space-y-6" data-id="element-606">
            <p data-id="element-607">
              El próximo lunes podés seguir yendo al trabajo sintiéndote
              atrapado/a en una vida que no elegiste.
            </p>
            <p data-id="element-608">
              O podés tener desde hoy el sistema que te muestra exactamente por
              dónde empezar a cambiarlo.
            </p>
            <p className="font-bold text-[#0b1628] text-2xl" data-id="element-609">
              La diferencia empieza con una decisión.
            </p>
          </motion.div>
        </div>
      </section>

      <PurchaseSection id="purchase-section" data-id="element-610" />

      {/* ============================================ */}
      {/* [12] FAQ — Updated content */}
      {/* ============================================ */}
      <section className="bg-blue-50/50 py-20 px-4" data-id="element-611">
        <div className="max-w-3xl mx-auto" data-id="element-612">
          <motion.div {...fadeUp} data-id="element-613">
            <h2 className="text-3xl md:text-4xl font-extrabold text-[#0b1628] mb-12 text-center" data-id="element-614">
              Preguntas frecuentes
            </h2>
          </motion.div>

          <div className="space-y-4" data-id="element-615">
            <FAQItem question="¿Qué formato tiene el kit?" answer="Es un producto 100% digital en formato PDF. Lo podés leer desde el celular, tablet o computadora. No necesitás ningún programa especial." data-id="element-616" />
            <FAQItem question="¿Cuándo recibo el material?" answer="Inmediatamente después de confirmar el pago. Te llega un email con el acceso. En menos de 2 minutos ya estás adentro." data-id="element-617" />
            <FAQItem question="¿Necesito conocimientos previos?" answer="Cero. Está diseñado para que cualquier persona pueda aplicarlo desde el primer día, sin importar tu punto de partida ni tu situación actual." data-id="element-618" />
            <FAQItem question="¿Incluye los 3 bonos?" answer="Sí. Al adquirir el kit recibís automáticamente los 3 bonos: Reset Personal, Mente en Calma y Control Total. Sin ningún costo adicional." data-id="element-619" />
            <FAQItem question="¿El pago es único o es una suscripción?" answer="Pago único. Pagás una sola vez y el acceso es de por vida. Sin cargos mensuales. Sin renovaciones. Sin sorpresas." data-id="element-620" />
            <FAQItem question="¿Qué pasa si no me sirve?" answer="Tenés 30 días de garantía total. Si sentís que el sistema no te aportó claridad ni dirección, te devolvemos el 100% de tu dinero. Sin preguntas. Sin formularios. Sin vueltas." data-id="element-621" />
            <FAQItem question="¿Si el producto se actualiza tengo que volver a pagar?" answer="No. Los que compran durante el precio de lanzamiento tienen acceso garantizado a todas las actualizaciones y mejoras futuras sin ningún costo extra. Comprás una vez, recibís todo lo que viene." data-id="element-622" />
          </div>
        </div>
      </section>

      {/* ============================================ */}
      {/* 13. FOOTER */}
      {/* ============================================ */}
      <footer className="bg-[#0b1628] text-slate-500 py-8 text-center text-sm" data-id="element-623">
        <p data-id="element-624">
          © 2026 Kit de Rendimiento Personal. Todos los derechos reservados.
        </p>
      </footer>
    </div>;
}